import { faLevelUp, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button } from '@ygtechnologies/rubus-ui';
import { CardSection, SimpleCard } from '@/components/Cards';
import { FileUploadWizard } from '@/components/FileUploadWizard';
import { Modal, ModalHeader, BadgeList, Banner, BorderBadge, Checkbox, FileIcon, FileUpload, SimpleWizard, SimpleWizardStep, TextArea, Tooltip } from '@ygtechnologies/bygr-frontend-component-library';
import { groupBy } from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { sendMultiMessage } from '@/services/comments';
import { IState } from '@/state/IState';
import { ResidentialUnitExtraData } from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { useAsyncState } from '@/utils/hooks';
import theme from './SendResidentialUnitMessageModal.module.scss';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { UnitSelector } from '../../UnitSelector';

interface SendResidentialUnitMessageModalProps {
    isOpen: boolean;
    toggle(): void;
    projectId: number;
}

enum Step {
    SelectUnits,
    AddMessage,
}

const Steps = [Step.SelectUnits, Step.AddMessage];

const SendResidentialUnitMessageModal: React.FC<SendResidentialUnitMessageModalProps> = ({ isOpen, toggle, projectId }) => {
    const [currentStep, setCurrentStep] = useState(Step.SelectUnits);
    const [selectedUnits, setSelectedUnits] = useState<number[]>([]);
    const [message, setMessage] = useState('');

    const [sendSms, setSendSms] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);
    const [attachment, setAttachment] = useState<FileData | null>(null);
    const [fileUploadWizardIsOpen, setFileUploadWizardIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const profileId = useSelector((state: IState) => state.global.activeProfile!.id);

    const [unitData] = useAsyncState(() => getResidentialUnitsByProjectId(projectId), [projectId], null);

    const reset = () => {
        setSendSms(false);
        setSendEmail(false);
        setAttachment(null);
        setCurrentStep(Step.SelectUnits);
        setSelectedUnits([]);
        setIsLoading(false);
    };

    const handleCancelClick = () => {
        toggle();
    };
    const handleSubmit = async () => {
        setIsLoading(true);

        await sendMultiMessage({
            profileId: profileId,
            notifyBySms: sendSms,
            notifyByEmail: sendEmail,
            residentialUnitIds: selectedUnits,
            attachment: attachment,
            message: message,
        });
        setIsLoading(false);
        toggle();
    };

    const fixedGroups = useMemo(() => {
        let tempGroups: {
            id: any;
            name: any;
            units: ResidentialUnitExtraData[];
        }[] = [];
        const units = unitData?.filter((x) => selectedUnits.includes(x.id));
        const groups = groupBy(units, 'residentialUnitGroupId');
        for (const key in groups) {
            const group: any = groups[key];
            const transformedGroup = {
                id: group[0].residentialUnitGroupId || 0,
                name: group[0].residentialUnitGroupName || <Trans>Other</Trans>,
                units: groups[key],
            };
            tempGroups.push(transformedGroup);
        }
        return tempGroups;
    }, [unitData, selectedUnits]);

    return (
        <>
            <Modal className={theme.modal} onClose={reset} size="xl" toggle={toggle} isOpen={isOpen}>
                <ModalHeader>
                    <Trans>Send a message to several units</Trans>
                </ModalHeader>
                <SimpleWizard
                    saveButtonProps={{ loading: isLoading, children: <Trans>Send</Trans> }}
                    steps={Steps}
                    currentStep={currentStep}
                    onStepChange={setCurrentStep}
                    onCancel={handleCancelClick}
                    onSubmit={handleSubmit}>
                    <SimpleWizardStep
                        nextButton={
                            selectedUnits.length === 0
                                ? () => (
                                      <Button variant="primary" disabled>
                                          <Trans>Next</Trans>
                                      </Button>
                                  )
                                : undefined
                        }
                        stepNumber={Step.SelectUnits}>
                        <UnitSelector
                            projectId={projectId!}
                            selectedResidentialUnitIds={selectedUnits}
                            onChange={setSelectedUnits}
                        />
                    </SimpleWizardStep>
                    <SimpleWizardStep stepNumber={Step.AddMessage}>
                        <div className="form-group">
                            <label>
                                <Trans>Units</Trans>
                            </label>
                            {fixedGroups.map((g) => (
                                <BadgeList key={g.id}>
                                    <BorderBadge size="small">{g.name}</BorderBadge>
                                    <FontAwesomeIcon className={theme.icon} icon={faLevelUp} rotation={90} />
                                    {g.units.map((u) => (
                                        <Tooltip
                                            key={u.id}
                                            content={
                                                u.buyerInfo.lastVisitDate === null ? <Trans>Unit has no buyer</Trans> : null
                                            }>
                                            <BorderBadge
                                                size="small"
                                                color={u.buyerInfo.lastVisitDate === null ? 'warning' : undefined}>
                                                <span>{u.name}</span>
                                            </BorderBadge>
                                        </Tooltip>
                                    ))}
                                </BadgeList>
                            ))}
                            {fixedGroups.flatMap((x) => x.units).some((x) => x.buyerInfo.lastVisitDate === null) && (
                                <Banner type="danger">
                                    <Trans>
                                        <strong>Please note</strong>, some of the units have no buyers. Those units are
                                        indicated above. <br /> The message will not be visible to buyers who are added or
                                        accept the invitation after this message is sent.
                                    </Trans>
                                </Banner>
                            )}
                            <label>
                                <Trans>Message</Trans>
                            </label>
                            <TextArea
                                placeholder={t`Message`}
                                className="form-control"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                minRows={2}
                            />
                            {/* <CommentInput
                                value={editorState}
                                onChange={handleMessageChange}
                                mentionsDirection="down"
                                // onSubmit={submit}
                                onFilePaste={handleFilesDrop}
                                mentions={[]}
                                className={theme.input}
                            /> */}
                            {attachment ? (
                                <SimpleCard className={theme.fileCard}>
                                    <FileIcon className={theme.fileIcon} value={attachment} />
                                    <CardSection caption={attachment.getFileType()}>{attachment.fileName}</CardSection>
                                    <Button
                                        size="sm"
                                        variant="tertiary"
                                        onClick={() => setAttachment(null)}
                                        icon={faTrashAlt}
                                    />
                                </SimpleCard>
                            ) : (
                                <FileUpload
                                    containerClassName={theme.fileUpload}
                                    onDrop={(f) => {
                                        setAttachment(new FileData(f[0]));
                                        setFileUploadWizardIsOpen(true);
                                    }}
                                    single
                                />
                            )}

                            <Checkbox onChange={(e) => setSendEmail(e.target.checked)} checked={sendEmail}>
                                <Trans>Send email notification</Trans>
                                <InfoIconWithTooltip
                                    title={<Trans>Send email notification</Trans>}
                                    content={
                                        <Trans>
                                            Please note, this will send a notification to the buyer informing them that they
                                            have a message only and will not include a preview of the message itself.
                                        </Trans>
                                    }></InfoIconWithTooltip>
                            </Checkbox>
                            <Checkbox onChange={(e) => setSendSms(e.target.checked)} checked={sendSms}>
                                <Trans>Send SMS notification</Trans>
                                <InfoIconWithTooltip
                                    title={<Trans>Send sms notification</Trans>}
                                    content={
                                        <Trans>
                                            Please note, this will send a notification to the buyer informing them that they
                                            have a message only and will not include a preview of the message itself.
                                        </Trans>
                                    }></InfoIconWithTooltip>
                            </Checkbox>
                        </div>
                    </SimpleWizardStep>
                </SimpleWizard>
            </Modal>
            <FileUploadWizard
                imageEditContinueButtonText={<Trans>Next</Trans>}
                initialFileData={attachment}
                onFileChange={(file) => setAttachment(file[0])}
                isOpen={fileUploadWizardIsOpen}
                toggle={() => {
                    setFileUploadWizardIsOpen(false);
                }}
            />
        </>
    );
};

export default SendResidentialUnitMessageModal;
